import React, { useState, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";
import moment, { Moment } from "moment";

import { Tooltip, ToggleButton } from "../../../../components";
import { LoadingSpinner } from "../../../../components";

import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { fetchDashboardInsgihtThunk } from "../../../../features/dashboard/dashboard.thunk";
import { selectInsight } from "../../../../features/dashboard/dashboard.selector";
import { sightUnit } from "../../../../features/dashboard/dashboard.states";

const ranges = [
  { label: "7", days: 7, tooltip: "Average 7 days" },
  { label: "30", days: 30, tooltip: "Average 30 days" },
  { label: "90", days: 90, tooltip: "Average 90 days" },
  { label: "180", days: 180, tooltip: "Average 180 days" },
  { label: "365", days: 365, tooltip: "Average 365 days" },
];

const buildTimeline = (days: number): Moment[] => {
  const today = moment().startOf("day");
  return Array.from({ length: days }, (_, i) =>
    today.clone().subtract(days - 1 - i, "days"),
  );
};

const mapCounts = (timeline: Moment[], raw: sightUnit[] = []): number[] => {
  return timeline.map((d) => {
    const hit = raw?.find((r) => moment(r.date).isSame(d, "day"));
    return hit ? hit.count : 0;
  });
};

const ScanInsightsChart: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<number>(30);
  const dispatch = useAppDispatch();

  const { analyzerData, scannerData, loadingDashboardInsight } =
    useAppSelector(selectInsight);

  /* Fetch on range change */
  useEffect(() => {
    dispatch(fetchDashboardInsgihtThunk(selectedRange));
  }, [selectedRange, dispatch]);

  /* Build the timeline */
  const timeline = useMemo(() => buildTimeline(selectedRange), [selectedRange]);

  /* Map analyzer + scanner counts onto that timeline */
  const analyzerSeriesData = useMemo(
    () => mapCounts(timeline, analyzerData),
    [timeline, analyzerData],
  );
  const scannerSeriesData = useMemo(
    () => mapCounts(timeline, scannerData ?? []),
    [timeline, scannerData],
  );

  /* Calculate min and max dates for proper axis alignment */
  const minDate = useMemo(() => {
    return timeline.length > 0 ? timeline[0].valueOf() : 0;
  }, [timeline]);

  const maxDate = useMemo(() => {
    return timeline.length > 0 ? timeline[timeline.length - 1].valueOf() : 0;
  }, [timeline]);

  /* Prepare series data with proper datetime values */
  const analyzerSeriesDataWithDates = useMemo(
    () =>
      timeline.map((date, index) => [
        date.valueOf(),
        analyzerSeriesData[index],
      ]),
    [timeline, analyzerSeriesData],
  );

  const scannerSeriesDataWithDates = useMemo(
    () =>
      timeline.map((date, index) => [date.valueOf(), scannerSeriesData[index]]),
    [timeline, scannerSeriesData],
  );

  /* Highcharts config */
  const options: Highcharts.Options = {
    chart: {
      type: "area",
      height: 300,
      spacing: [10, 0, 0, 0],
      plotBorderColor: "#e6e6e6",
      plotBorderWidth: 1,
    },
    title: {
      text: "",
      align: "left",
      style: { fontWeight: "bold", fontSize: "16px" },
    },
    xAxis: {
      type: "datetime",
      min: minDate,
      max: maxDate,
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 1,
      gridLineColor: "#e0e7ef",
      labels: {
        rotation: 0,
        align: "center",
        autoRotationLimit: 0,
        style: { fontSize: "11px" },
        format: "{value:%b}<br/>{value:%d}",
      },
      accessibility: {
        rangeDescription: "Dates",
      },
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: { text: undefined },
      labels: { style: { fontSize: "11px" } },
      gridLineWidth: 0,
    },
    legend: { enabled: true },
    tooltip: {
      shared: true,
      xDateFormat: "%b %d, %Y",
    },
    plotOptions: {
      area: {
        fillOpacity: 0.2,
        marker: { enabled: true, radius: 2 },
        lineWidth: 2,
      },
    },
    series: [
      {
        type: "area",
        name: "Analyzer",
        data: analyzerSeriesDataWithDates,
        color: "#2196f3",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(33,150,243,0.3)"],
            [1, "rgba(33,150,243,0)"],
          ],
        },
      } as Highcharts.SeriesAreaOptions,
      {
        type: "area",
        name: "Scanner",
        data: scannerSeriesDataWithDates,
        color: "#e91e63",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(233,30,99,0.3)"],
            [1, "rgba(233,30,99,0)"],
          ],
        },
      } as Highcharts.SeriesAreaOptions,
    ],
    credits: { enabled: false },
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 1,
        border: "1px solid #e3e3e3",
        p: "10px",
      }}
    >
      <Typography variant="h4" fontWeight={700} mb={1}>
        Scan Insights
      </Typography>

      {/* Range buttons */}
      <Box sx={{ mb: 1, display: "flex", justifyContent: "center", gap: 1 }}>
        {ranges.map(({ label, days, tooltip }) => (
          <Tooltip key={label} title={tooltip}>
            <ToggleButton
              value={days}
              selected={selectedRange === days}
              onClick={() => setSelectedRange(days)}
            >
              {label}
            </ToggleButton>
          </Tooltip>
        ))}
      </Box>

      {/* Chart / loader */}
      {loadingDashboardInsight ? (
        <Box
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <LoadingSpinner />
        </Box>
      ) : (
        <HighchartsReact
          key={selectedRange}
          highcharts={Highcharts}
          options={options}
        />
      )}
    </Box>
  );
};

export default ScanInsightsChart;
